/**
 * hero-video-banner.js
 * Lightweight video slider for sections/hero-video-banner.liquid.
 * - Single slide: loop video, no navigation.
 * - Multiple slides: advance on video ended, dots + swipe, carousel loops.
 * - Deferred video injection from <template> for non-first slides.
 * - Pauses when scrolled out of view (IntersectionObserver).
 * - Cleans up listeners on theme customizer section reload/unload (destroy).
 * - Image-only slides auto-advance after a fixed duration.
 * - Auto-advances when a slide video is missing or fails to play.
 * - Debounces rapid goTo calls from dots/swipes.
 */
(function () {
  const SWIPE_THRESHOLD = 48;
  const GO_TO_DEBOUNCE_MS = 150; // Prevents flicker from rapid dot clicks / swipes
  const IMAGE_SLIDE_ADVANCE_MS = 5000; // Display time for image-only slides
  const VIDEO_ERROR_ADVANCE_MS = 3000; // Skip stuck slides after missing/broken video

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class HeroVideoBanner {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-hero-video-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-hero-video-dot]'));
      this.slideCount = this.slides.length;
      this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (this.currentIndex < 0) this.currentIndex = 0;

      this.autoplay = root.dataset.autoplay !== 'false';
      this.singleSlide = this.slideCount <= 1;
      this.isTransitioning = false;
      this.isInView = true; // Tracked by IntersectionObserver; blocks play while off-screen
      this.touchStartX = 0;
      this._goToTimer = null; // Debounce timer for goTo()
      this._errorAdvanceTimer = null; // Fallback advance when video missing or play() fails

      this._onEnded = this._onEnded.bind(this);
      this._onPlaying = this._onPlaying.bind(this);
      this._onVideoError = this._onVideoError.bind(this);
      this._onDotClick = this._onDotClick.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onIntersection = this._onIntersection.bind(this);

      this._bindEvents();
      this._observeVisibility();
      this._activateSlide(this.currentIndex, { initial: true });
      this._preloadNext(this.currentIndex);
    }

    _bindEvents() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', this._onDotClick);
      });

      if (this.slideCount > 1) {
        this.root.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.root.addEventListener('touchend', this._onTouchEnd, { passive: true });
      }

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this.root.addEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    // Pause video when hero scrolls out of view; resume when it comes back.
    _observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.intersectionObserver = new IntersectionObserver(this._onIntersection, {
        threshold: 0.25,
      });
      this.intersectionObserver.observe(this.root);
    }

    _onIntersection(entries) {
      entries.forEach((entry) => {
        this.isInView = entry.isIntersecting;

        const slide = this.slides[this.currentIndex];
        if (!slide) return;

        const video = this._getVideo(slide);
        if (!video) return;

        if (entry.isIntersecting) {
          if (!this.autoplay || prefersReducedMotion()) return;

          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => this._scheduleErrorAdvance(slide));
          }
        } else {
          video.pause();
          video.classList.remove('is-playing');
        }
      });
    }

    _onBlockSelect(event) {
      const slide = event.target.closest('[data-hero-video-slide]');
      if (!slide || !this.root.contains(slide)) return;
      const index = this.slides.indexOf(slide);
      if (index >= 0) this.goTo(index);
    }

    _onDotClick(event) {
      const index = Number(event.currentTarget.dataset.heroVideoDot);
      if (!Number.isNaN(index)) this.goTo(index);
    }

    _onTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX;
    }

    _onTouchEnd(event) {
      const delta = event.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(delta) < SWIPE_THRESHOLD) return;
      if (delta < 0) this.goTo((this.currentIndex + 1) % this.slideCount);
      else this.goTo((this.currentIndex - 1 + this.slideCount) % this.slideCount);
    }

    _onEnded(event) {
      if (this.singleSlide) return;
      const slide = event.target.closest('[data-hero-video-slide]');
      if (!slide || this.slides.indexOf(slide) !== this.currentIndex) return;
      if (!this.autoplay || prefersReducedMotion()) return;
      this.goTo((this.currentIndex + 1) % this.slideCount);
    }

    _onPlaying(event) {
      event.target.classList.add('is-playing');
      clearTimeout(this._errorAdvanceTimer); // Playback succeeded — cancel fallback advance
    }

    // Video load/play failed — advance to next slide after a short delay.
    _onVideoError(event) {
      const slide = event.target.closest('[data-hero-video-slide]');
      if (!slide || this.slides.indexOf(slide) !== this.currentIndex) return;
      this._scheduleErrorAdvance(slide);
    }

    // Used when video element is missing, errors, or play() rejects.
    _scheduleErrorAdvance(slide) {
      const delay = slide?.dataset.imageSlide === 'true'
        ? IMAGE_SLIDE_ADVANCE_MS
        : VIDEO_ERROR_ADVANCE_MS;
      this._scheduleAutoAdvance(slide, delay);
    }

    _scheduleAutoAdvance(slide, delay) {
      if (this.singleSlide) return;
      if (slide && this.slides.indexOf(slide) !== this.currentIndex) return;

      clearTimeout(this._errorAdvanceTimer);
      this._errorAdvanceTimer = setTimeout(() => {
        if (!this.autoplay || prefersReducedMotion()) return;
        this._goToNow((this.currentIndex + 1) % this.slideCount); // Bypass debounce — don't delay recovery
      }, delay);
    }

    _getVideo(slide) {
      return slide.querySelector('.hero-video-banner__video');
    }

    _injectVideo(slide) {
      const media = slide.querySelector('.hero-video-banner__media');
      if (!media || media.querySelector('.hero-video-banner__video')) return this._getVideo(slide);

      const template = media.querySelector('.hero-video-banner__video-template');
      if (!template) return this._getVideo(slide);

      const video = template.content.firstElementChild.cloneNode(true);
      media.appendChild(video);
      template.remove();
      return video;
    }

    _wireVideo(video) {
      video.addEventListener('ended', this._onEnded);
      video.addEventListener('playing', this._onPlaying);
      video.addEventListener('error', this._onVideoError); // Catch missing/broken sources
    }

    // Mirror of _wireVideo — called from destroy() to prevent duplicate listeners.
    _unwireVideo(video) {
      video.removeEventListener('ended', this._onEnded);
      video.removeEventListener('playing', this._onPlaying);
      video.removeEventListener('error', this._onVideoError);
    }

    _pauseSlide(slide, { reset = true } = {}) {
      const video = this._getVideo(slide);
      if (!video) return;
      video.pause();
      if (reset) video.currentTime = 0;
      video.classList.remove('is-playing');
    }

    _playSlide(slide) {
      const video = this._injectVideo(slide) || this._getVideo(slide);
      if (!video) {
        this._scheduleErrorAdvance(slide);
        return;
      }

      if (!video.dataset.wired) {
        this._wireVideo(video);
        video.dataset.wired = 'true';
      }

      if (!this.autoplay || prefersReducedMotion() || !this.isInView) return;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => this._scheduleErrorAdvance(slide));
      }
    }

    _preloadNext(index) {
      if (this.singleSlide) return;
      const nextSlide = this.slides[(index + 1) % this.slideCount];
      if (!nextSlide) return;

      const video = this._injectVideo(nextSlide);
      if (!video || video.readyState > 0) return;

      video.preload = 'metadata';
      video.load();
      video.pause();
    }

    _updateDots(index) {
      this.dots.forEach((dot, i) => {
        const isCurrent = i === index;
        dot.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      });
    }

    _activateSlide(index, { initial = false } = {}) {
      const slide = this.slides[index];
      if (!slide) return;

      clearTimeout(this._errorAdvanceTimer);

      this.slides.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        item.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });

      this.slides.forEach((item, i) => {
        if (i !== index) this._pauseSlide(item);
      });

      this.currentIndex = index;
      this._updateDots(index);
      const imageSlide = slide.dataset.imageSlide === 'true';
      this.root.classList.toggle('is-image-slide', imageSlide);
      if (imageSlide) {
        if (slide.dataset.desktopAspect) this.root.style.setProperty('--image-desktop-aspect', slide.dataset.desktopAspect);
        if (slide.dataset.mobileAspect) this.root.style.setProperty('--image-mobile-aspect', slide.dataset.mobileAspect);
      }

      const video = this._injectVideo(slide) || this._getVideo(slide);
      if (video && !video.dataset.wired) {
        this._wireVideo(video);
        video.dataset.wired = 'true';
      }

      this._playSlide(slide);

      if (!initial) this._preloadNext(index);
    }

    // Public API — debounced so rapid dot clicks / swipes don't stack transitions.
    goTo(index) {
      clearTimeout(this._goToTimer);
      this._goToTimer = setTimeout(() => {
        this._goToNow(index);
      }, GO_TO_DEBOUNCE_MS);
    }

    // Immediate slide change (used internally and for error auto-advance).
    _goToNow(index) {
      if (this.slideCount <= 1) return;
      if (index === this.currentIndex || this.isTransitioning) return;
      if (index < 0 || index >= this.slideCount) return;

      this.isTransitioning = true;
      const previousSlide = this.slides[this.currentIndex];
      this._pauseSlide(previousSlide);
      this._activateSlide(index);
      this.isTransitioning = false;
    }

    // Tear down all listeners/observers — required before re-init in theme customizer.
    destroy() {
      clearTimeout(this._goToTimer);
      clearTimeout(this._errorAdvanceTimer);

      this.dots.forEach((dot) => {
        dot.removeEventListener('click', this._onDotClick);
      });

      if (this.slideCount > 1) {
        this.root.removeEventListener('touchstart', this._onTouchStart);
        this.root.removeEventListener('touchend', this._onTouchEnd);
      }

      this.root.removeEventListener('shopify:block:select', this._onBlockSelect);
      this.intersectionObserver?.disconnect();

      this.slides.forEach((slide) => {
        const video = this._getVideo(slide);
        if (video && video.dataset.wired) {
          this._unwireVideo(video);
        }
        this._pauseSlide(slide);
      });

      delete this.root.heroVideoBanner;
    }

    static initAll() {
      document.querySelectorAll('[data-hero-video-banner]').forEach((root) => {
        if (root.heroVideoBanner) return;
        root.heroVideoBanner = new HeroVideoBanner(root);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', HeroVideoBanner.initAll);
  } else {
    HeroVideoBanner.initAll();
  }

  // Theme customizer: destroy old instance before re-init to avoid duplicate listeners.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-hero-video-banner]');
    if (root) {
      root.heroVideoBanner?.destroy();
      root.heroVideoBanner = new HeroVideoBanner(root);
    }
  });

  // Theme customizer: clean up when section is removed or replaced.
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-hero-video-banner]');
    root?.heroVideoBanner?.destroy();
  });
})();
